import React, { Children, isValidElement, useRef } from 'react'

import {
  DataTableRootProps,
  DataTableRowProps,
} from 'components/DataTable/types'
import { Table } from 'components/Table'

import { ClosestScrollableParentProvider } from './ClosestScrollableParentContext'
import { ReachScrollBottomNotifier } from './ReachScrollBottomNotifier'
import { RegularTableBody } from './RegularTableBody'
import { VirtualTableBody } from './VirtualTableBody'

const DataTableRoot = <T extends object>({
  value,
  rowComponent,
  children,
  isLoading,
  notFoundMessage = 'No items found.',
  virtualScroll = false,
  onScrollBottomReached,
  ...props
}: DataTableRootProps<T>) => {
  const tableRef = useRef<HTMLTableElement>(null)

  Children.forEach(children, (child) => {
    if (!isValidElement(child) || ![Table.Header].includes(child.type as any)) {
      throw new Error(
        `Invalid child component '${child.type}'. Please use 'Table.Header' or 'DataTable.Header' only`,
      )
    }
  })

  const columnCount = Children.count(children)

  return (
    <ClosestScrollableParentProvider tableRef={tableRef}>
      <Table.Root {...props} ref={tableRef}>
        <Table.Head>
          <Table.Row>{children}</Table.Row>
        </Table.Head>

        <Table.Body>
          {!isLoading && !value.length && (
            <Table.Cell colSpan={columnCount}>{notFoundMessage}</Table.Cell>
          )}
          {!!virtualScroll ? (
            <VirtualTableBody
              value={value}
              rowComponent={rowComponent}
              columnCount={columnCount}
              tableRef={tableRef}
              virtualScroll={virtualScroll}
            />
          ) : (
            <RegularTableBody value={value} rowComponent={rowComponent} />
          )}
          {isLoading && (
            <>
              <Table.RowSkeleton colSpan={columnCount} />
              <Table.RowSkeleton colSpan={columnCount} />
              <Table.RowSkeleton colSpan={columnCount} />
              <Table.RowSkeleton colSpan={columnCount} />
              <Table.RowSkeleton colSpan={columnCount} />
            </>
          )}
        </Table.Body>
      </Table.Root>
      {!!onScrollBottomReached && (
        <ReachScrollBottomNotifier
          isLoading={isLoading}
          onScrollBottomReached={onScrollBottomReached}
        />
      )}
    </ClosestScrollableParentProvider>
  )
}

const DataTable = {
  ...Table,
  Root: DataTableRoot,
}

export { DataTable }

export type { DataTableRootProps, DataTableRowProps }
